// Service Desk metrics dashboard: volume over time, response/resolution speed,
// breakdowns by brand/category/priority, open vs closed and SLA compliance.
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import { useTheme } from "../../../context/ThemeContext";
import {
  getMetrics, getSettings, formatMins,
  type Metrics, type ServiceDeskSettings,
} from "../services/serviceDeskService";

const COLORS = ["#3B82F6", "#8B5CF6", "#10B981", "#F59E0B", "#EF4444", "#06B6D4", "#EC4899"];

export default function DashboardSection() {
  const { t } = useTranslation("serviceDesk");
  const { isDarkMode } = useTheme();
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [settings, setSettings] = useState<ServiceDeskSettings | null>(null);
  const [brand, setBrand] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { getSettings().then(setSettings).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    getMetrics(brand ? { brand } : {}).then(setMetrics).finally(() => setLoading(false));
  }, [brand]);

  const categoryLabel = useMemo(() => {
    const map: Record<string, string> = {};
    (settings?.categories || []).forEach((c) => { map[c.id] = c.label; });
    return map;
  }, [settings]);
  const labelLabel = useMemo(() => {
    const map: Record<string, string> = {};
    (settings?.labels || []).forEach((l) => { map[l.id] = l.label; });
    return map;
  }, [settings]);

  const card = isDarkMode ? "bg-dark-surface border-dark-border" : "bg-white border-gray-200";
  const text = isDarkMode ? "text-white" : "text-gray-900";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";

  // Guard the whole `totals` sub-object, not just `metrics` — a partial/legacy
  // payload (truthy but without `totals`) must not crash the dashboard.
  if (loading || !metrics?.totals) return <div className="p-8 text-center text-gray-400">{t("dashboard.loading")}</div>;

  // Defensive defaults so a missing series never throws inside recharts.
  const volumeByDay = metrics.volumeByDay || [];
  const byBrand = metrics.byBrand || [];
  const byCategory = metrics.byCategory || [];
  const byPriority = metrics.byPriority || [];
  const byLabel = metrics.byLabel || [];

  const kpis = [
    { label: t("dashboard.kpis.totalTickets"), value: metrics.totals.all },
    { label: t("dashboard.kpis.open"), value: metrics.totals.open },
    { label: t("dashboard.kpis.closed"), value: metrics.totals.closed },
    { label: t("dashboard.kpis.avgFirstResponse"), value: formatMins(metrics.avgFirstResponseMins) },
    { label: t("dashboard.kpis.avgResolution"), value: formatMins(metrics.avgResolutionMins) },
    { label: t("dashboard.kpis.slaCompliance"), value: metrics.slaCompliancePct != null ? `${metrics.slaCompliancePct}%` : "—" },
  ];

  // Cierre por solicitante (#729). Van en su propia fila: no son desempeño del
  // equipo (los de arriba) sino lo que tarda el cliente, y mezclados se leerían
  // como un SLA que la mesa incumple.
  const closureKpis: { label: string; value: string | number; hint?: string }[] = [
    { label: t("dashboard.kpis.avgWaitingCustomer"), value: formatMins(metrics.avgWaitingCustomerMins ?? null) },
    { label: t("dashboard.kpis.avgRequesterClosure"), value: formatMins(metrics.avgRequesterClosureMins ?? null) },
    {
      label: t("dashboard.kpis.pendingClosure"),
      value: metrics.pendingClosure ?? "—",
      hint: metrics.pendingClosure != null
        ? t("dashboard.kpis.pendingClosureUnlocked", { n: metrics.pendingClosureUnlocked ?? 0 })
        : undefined,
    },
    {
      label: t("dashboard.kpis.reworkedTickets"),
      value: metrics.reworkedTickets ?? "—",
      hint: metrics.reworkedTickets != null
        ? t("dashboard.kpis.reworkTotal", { n: metrics.reworkTotal ?? 0 })
        : undefined,
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className={`text-xl font-bold ${text}`}>{t("dashboard.title")}</h1>
        <select
          className={`rounded-lg border px-3 py-1.5 text-sm ${isDarkMode ? "!bg-gray-800 !border-gray-600 !text-white" : "!bg-white !border-gray-300"}`}
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
        >
          <option value="">{t("dashboard.allBrands")}</option>
          {(settings?.brands || []).map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
        </select>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {kpis.map((k) => (
          <div key={k.label} className={`rounded-xl border p-4 ${card}`}>
            <p className={`text-xs ${muted}`}>{k.label}</p>
            <p className={`mt-1 text-2xl font-bold ${text}`}>{k.value}</p>
          </div>
        ))}
      </div>

      <h2 className={`mb-2 text-sm font-semibold ${text}`}>{t("dashboard.closureTitle")}</h2>
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {closureKpis.map((k) => (
          <div key={k.label} className={`rounded-xl border p-4 ${card}`}>
            <p className={`text-xs ${muted}`}>{k.label}</p>
            <p className={`mt-1 text-2xl font-bold ${text}`}>{k.value}</p>
            {k.hint && <p className={`mt-0.5 text-xs ${muted}`}>{k.hint}</p>}
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title={t("dashboard.charts.volumeOverTime")} card={card} text={text}>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={volumeByDay}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#3B82F6" strokeWidth={2} dot={false} name="Tickets" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("dashboard.charts.byBrand")} card={card} text={text}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={byBrand}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#8B5CF6" name="Tickets" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("dashboard.charts.byCategory")} card={card} text={text}>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={byCategory.map((c) => ({ name: categoryLabel[c.key] || c.key, value: c.count }))}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={90}
                label
              >
                {byCategory.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t("dashboard.charts.byPriorityLabel")} card={card} text={text}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={[
              ...byPriority.map((p) => ({ name: p.key, count: p.count })),
              ...byLabel.map((l) => ({ name: labelLabel[l.key] || l.key, count: l.count })),
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#10B981" name="Tickets" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

function ChartCard({ title, card, text, children }: { title: string; card: string; text: string; children: React.ReactNode }) {
  return (
    <div className={`rounded-xl border p-4 ${card}`}>
      <h2 className={`mb-3 text-sm font-semibold ${text}`}>{title}</h2>
      {children}
    </div>
  );
}
